import { useTranslation } from 'react-i18next';
import { Popover } from '@c2fo/liquidity';
import { ArrowsRotateLightIcon, BoltLightningLightIcon, CalendarCheckLightIcon } from '@c2fo/liquidity/icons';
import { OfferBuilderFrequency, WorkingTakerMarket } from '@/enterprise/components/OfferBuilder/OfferBuilderContext';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';

const usePricingTypeDisplay = () => {
  const { t } = useTranslation();

  const getPricingTypeDisplay = (takerMarket?: TakerMarket | null) => {
    if (!takerMarket?.marketIsEnabled) {
      return t('core.inactiveMarket');
    }

    if (takerMarket?.offerConfig.offerType === 'PREFERRED_TERM') {
      return t('core.preferredRate');
    }

    switch (takerMarket?.marketPricingType) {
      case 'PRICE_DISCOVERY':
        return t('core.nameYourRate');
      case 'STATIC':
        return t('core.fixedRate');
      case 'BENCHMARK':
        return t('core.variableRate');
      default:
        return null;
    }
  };

  const getPricingTypeDisplayWithDetail = (
    takerMarket: TakerMarket,
    frequency: OfferBuilderFrequency,
    workingTakerMarket?: WorkingTakerMarket
  ) => {
    const baseDisplay = getPricingTypeDisplay(takerMarket);

    if (!baseDisplay) {
      return null;
    }

    if (workingTakerMarket?.pricingStrategy === 'expressAccept') {
      return (
        <div className="flex items-center gap-1">
          <Popover>
            <Popover.Trigger asChild>
              <button className="cursor-pointer rounded-full p-0.5 hover:bg-black/10" type="button">
                <BoltLightningLightIcon className="size-5 fill-text-secondary" />
              </button>
            </Popover.Trigger>
            <Popover.Content arrow variant="dark">
              {t('builder.expressAcceptOffer')}
            </Popover.Content>
          </Popover>
          <span>{baseDisplay}</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1">
        {frequency === 'oneTime' ? (
          <Popover>
            <Popover.Trigger asChild>
              <button className="cursor-pointer rounded-full p-0.5 hover:bg-black/10" type="button">
                <CalendarCheckLightIcon className="size-5 fill-text-secondary" />
              </button>
            </Popover.Trigger>
            <Popover.Content arrow variant="dark">
              {t('builder.oneTimeOffer')}
            </Popover.Content>
          </Popover>
        ) : (
          <Popover>
            <Popover.Trigger asChild>
              <button className="cursor-pointer rounded-full p-0.5 hover:bg-black/10" type="button">
                <ArrowsRotateLightIcon className="size-5 fill-text-secondary" />
              </button>
            </Popover.Trigger>
            <Popover.Content arrow variant="dark">
              {t('builder.recurringOffer')}
            </Popover.Content>
          </Popover>
        )}
        <span>{baseDisplay}</span>
      </div>
    );
  };

  return { getPricingTypeDisplay, getPricingTypeDisplayWithDetail };
};

export default usePricingTypeDisplay;
